import { useState, useEffect, useRef } from 'react';

interface Message {
  id: string;
  userId: string;
  username: string;
  message: string;
  response?: string;
  timestamp: number;
  status: 'pending' | 'answered';
  type?: 'normal' | 'activation_request';
  screenshotProof?: string;
}

function lsGet<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch { return fallback; }
}

function lsSet(key: string, value: unknown): void {
  try { localStorage.setItem(key, JSON.stringify(value)); } catch { /* ignore */ }
}

export default function ServiceClientAdmin() {
  const [messages, setMessages] = useState<Message[]>([]);
  const [selectedMessage, setSelectedMessage] = useState<Message | null>(null);
  const [responseText, setResponseText] = useState('');
  const [filter, setFilter] = useState<'all' | 'pending' | 'answered' | 'activation'>('all');
  const [sending, setSending] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [newNotif, setNewNotif] = useState(0);
  const prevCountRef = useRef(0);
  const bottomRef = useRef<HTMLDivElement>(null);
  const [showScreenshotModal, setShowScreenshotModal] = useState(false);
  const [modalScreenshot, setModalScreenshot] = useState('');

  const [confirmActivate, setConfirmActivate] = useState<{ phone: string; name: string } | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [isActivating, setIsActivating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const loadMessages = () => {
    const all = lsGet<Message[]>('serviceClientMessages', []);
    const sorted = [...all].sort((a, b) => b.timestamp - a.timestamp);
    setMessages(sorted);
  };

  useEffect(() => {
    loadMessages();
    const interval = setInterval(loadMessages, 5000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const pending = messages.filter(m => m.status === 'pending').length;
    if (pending > prevCountRef.current) {
      setNewNotif(pending - prevCountRef.current);
      setTimeout(() => setNewNotif(0), 5000);
    }
    prevCountRef.current = pending;
  }, [messages]);

  useEffect(() => {
    if (selectedMessage) {
      const updated = messages.find(m => m.id === selectedMessage.id);
      if (updated) setSelectedMessage(updated);
    }
  }, [messages]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [selectedMessage]);

  const doActivateUserAccount = async (userPhone: string, userName: string) => {
    setIsActivating(true);
    try {
      const { siteKey } = await import('../../../utils/sharedStorage');

      // Mettre à jour dans toutes les clés possibles
      const keys = [siteKey(`user_${userPhone}`), `user_${userPhone}`];
      keys.forEach(k => {
        const raw = localStorage.getItem(k);
        if (raw) {
          try {
            const u = JSON.parse(raw);
            u.accountStatus = 'active';
            u.isActivated = true;
            u.activated = true;
            u.activatedAt = new Date().toISOString();
            localStorage.setItem(k, JSON.stringify(u));
          } catch { /* ignore */ }
        }
      });

      // Mettre à jour currentUser si c'est lui
      try {
        const raw = localStorage.getItem('currentUser') || '{}';
        const cu = JSON.parse(raw);
        if (cu.phone === userPhone) {
          cu.accountStatus = 'active';
          cu.isActivated = true;
          cu.activated = true;
          cu.activatedAt = new Date().toISOString();
          localStorage.setItem('currentUser', JSON.stringify(cu));
        }
      } catch { /* ignore */ }

      // Signal d'activation
      try {
        const ch = new BroadcastChannel('account_activation');
        ch.postMessage({ type: 'ACCOUNT_ACTIVATED', phone: userPhone, activatedAt: new Date().toISOString() });
        ch.close();
      } catch { /* ignore */ }

      // Réponse automatique dans le message
      if (selectedMessage) {
        const autoResponse = `✅ Votre compte a été activé avec succès ! Vous pouvez maintenant accéder à toutes les fonctionnalités de votre tableau de bord. Bienvenue dans SUCCESSLINK AGENCY !`;
        sendResponseToMessage(selectedMessage.id, autoResponse);
      }

      setSuccessMsg(`✅ Compte de ${userName} activé avec succès !`);
      setTimeout(() => setSuccessMsg(''), 4000);
    } catch {
      setSuccessMsg('❌ Erreur lors de l\'activation. Veuillez réessayer.');
      setTimeout(() => setSuccessMsg(''), 4000);
    } finally {
      setIsActivating(false);
      setConfirmActivate(null);
    }
  };

  const sendResponseToMessage = (messageId: string, response: string) => {
    const all = lsGet<Message[]>('serviceClientMessages', []);
    const updated = all.map(m =>
      m.id === messageId ? { ...m, response, status: 'answered' as const } : m
    );
    lsSet('serviceClientMessages', updated);
    loadMessages();
  };

  const sendResponse = () => {
    if (!selectedMessage || !responseText.trim()) return;
    setSending(true);
    sendResponseToMessage(selectedMessage.id, responseText.trim());
    setResponseText('');
    setSending(false);
    setSuccessMsg('Réponse envoyée avec succès !');
    setTimeout(() => setSuccessMsg(''), 3000);
  };

  const doDeleteMessage = (id: string) => {
    setIsDeleting(true);
    const all = lsGet<Message[]>('serviceClientMessages', []);
    const filtered = all.filter(m => m.id !== id);
    lsSet('serviceClientMessages', filtered);
    loadMessages();
    if (selectedMessage?.id === id) setSelectedMessage(null);
    setIsDeleting(false);
    setConfirmDelete(null);
  };

  const formatDate = (ts: number) => {
    try {
      return new Date(ts).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch { return ''; }
  };

  const filteredMessages = messages.filter(m => {
    if (filter === 'all') return true;
    if (filter === 'activation') return m.type === 'activation_request';
    return m.status === filter;
  });

  const pendingCount = messages.filter(m => m.status === 'pending').length;
  const answeredCount = messages.filter(m => m.status === 'answered').length;
  const activationCount = messages.filter(m => m.type === 'activation_request').length;

  return (
    <div>
      {/* Modale confirmation activation */}
      {confirmActivate && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 flex items-center justify-center bg-orange-100 rounded-full flex-shrink-0">
                <i className="ri-checkbox-circle-line text-2xl text-orange-600"></i>
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">Activer le compte</h3>
                <p className="text-sm text-gray-500">Confirmation requise</p>
              </div>
            </div>
            <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 mb-4">
              <p className="text-sm text-orange-800">
                Activer le compte de <strong>{confirmActivate.name}</strong> ({confirmActivate.phone}) ?
              </p>
              <p className="text-xs text-orange-600 mt-1">Le paiement a été vérifié et validé.</p>
            </div>
            <div className="flex gap-3">
              <button onClick={() => setConfirmActivate(null)} disabled={isActivating}
                className="flex-1 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold rounded-xl text-sm transition cursor-pointer whitespace-nowrap disabled:opacity-50">
                Annuler
              </button>
              <button onClick={() => doActivateUserAccount(confirmActivate.phone, confirmActivate.name)} disabled={isActivating}
                className="flex-1 py-3 bg-orange-600 hover:bg-orange-700 text-white font-bold rounded-xl text-sm transition cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 shadow disabled:opacity-50">
                {isActivating ? <><i className="ri-loader-4-line animate-spin"></i>Activation...</> : <><i className="ri-checkbox-circle-line"></i>Activer</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modale confirmation suppression */}
      {confirmDelete && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 flex items-center justify-center bg-red-100 rounded-full flex-shrink-0">
                <i className="ri-delete-bin-line text-2xl text-red-600"></i>
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">Supprimer le message</h3>
                <p className="text-sm text-gray-500">Action irréversible</p>
              </div>
            </div>
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-4">
              <p className="text-sm text-red-800">Supprimer ce message définitivement ?</p>
            </div>
            <div className="flex gap-3">
              <button onClick={() => setConfirmDelete(null)} disabled={isDeleting}
                className="flex-1 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold rounded-xl text-sm transition cursor-pointer whitespace-nowrap disabled:opacity-50">
                Annuler
              </button>
              <button onClick={() => doDeleteMessage(confirmDelete)} disabled={isDeleting}
                className="flex-1 py-3 bg-red-600 hover:bg-red-700 text-white font-bold rounded-xl text-sm transition cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 shadow disabled:opacity-50">
                {isDeleting ? <><i className="ri-loader-4-line animate-spin"></i>Suppression...</> : <><i className="ri-delete-bin-line"></i>Supprimer</>}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="mb-6 flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900 mb-1">Service Client</h2>
          <p className="text-sm text-gray-500">Gérez et répondez aux messages des utilisateurs</p>
        </div>
        <div className="flex items-center gap-2 text-xs text-green-600 bg-green-50 px-3 py-1.5 rounded-full border border-green-200">
          <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse inline-block"></span>
          Actualisation auto
        </div>
      </div>

      {newNotif > 0 && (
        <div className="mb-4 px-4 py-3 bg-yellow-50 border border-yellow-300 rounded-xl text-yellow-800 text-sm flex items-center gap-2 animate-pulse">
          <i className="ri-notification-3-line text-lg"></i>
          <strong>{newNotif} nouveau(x) message(s)</strong> reçu(s) !
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        {[
          { label: 'Total messages', value: messages.length, icon: 'ri-message-3-line', color: 'teal' },
          { label: 'En attente', value: pendingCount, icon: 'ri-time-line', color: 'yellow' },
          { label: 'Répondus', value: answeredCount, icon: 'ri-checkbox-circle-line', color: 'green' },
          { label: 'Demandes activation', value: activationCount, icon: 'ri-file-upload-line', color: 'orange' },
        ].map((s, i) => (
          <div key={i} className="bg-white rounded-xl shadow p-5 flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 mb-1">{s.label}</p>
              <p className={`text-2xl font-bold text-${s.color}-600`}>{s.value}</p>
            </div>
            <div className={`w-12 h-12 bg-${s.color}-100 rounded-full flex items-center justify-center`}>
              <i className={`${s.icon} text-2xl text-${s.color}-600`}></i>
            </div>
          </div>
        ))}
      </div>

      <div className="flex gap-2 mb-4 flex-wrap">
        {(['all', 'pending', 'answered', 'activation'] as const).map(f => (
          <button key={f} onClick={() => setFilter(f)}
            className={`px-4 py-2 rounded-lg text-sm font-semibold transition cursor-pointer whitespace-nowrap ${
              filter === f
                ? f === 'all' ? 'bg-gray-800 text-white' : f === 'pending' ? 'bg-yellow-500 text-white' : f === 'answered' ? 'bg-green-600 text-white' : 'bg-orange-600 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}>
            {f === 'all' && `Tous (${messages.length})`}
            {f === 'pending' && `En attente (${pendingCount})`}
            {f === 'answered' && `Répondus (${answeredCount})`}
            {f === 'activation' && `🔓 Demandes activation (${activationCount})`}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Liste des messages */}
        <div className="bg-white rounded-xl shadow overflow-hidden">
          <div className="p-4 border-b border-gray-100">
            <h3 className="font-bold text-gray-800 flex items-center gap-2">
              <i className="ri-inbox-line text-red-600"></i> Messages reçus
            </h3>
          </div>
          <div className="divide-y divide-gray-100 max-h-[580px] overflow-y-auto">
            {filteredMessages.length === 0 ? (
              <div className="p-10 text-center">
                <i className="ri-inbox-line text-5xl text-gray-200 mb-3 block"></i>
                <p className="text-gray-400 text-sm">Aucun message</p>
              </div>
            ) : (
              filteredMessages.map(msg => (
                <div key={msg.id} onClick={() => { setSelectedMessage(msg); setResponseText(msg.response || ''); }}
                  className={`p-4 cursor-pointer hover:bg-gray-50 transition ${selectedMessage?.id === msg.id ? 'bg-teal-50 border-l-4 border-teal-600' : ''}`}>
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div className="w-9 h-9 bg-teal-100 rounded-full flex items-center justify-center">
                        <i className="ri-user-line text-teal-600"></i>
                      </div>
                      <div>
                        <p className="text-sm font-bold text-gray-800">{msg.username}</p>
                        <p className="text-xs text-gray-400">{msg.userId}</p>
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${msg.status === 'answered' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                        {msg.status === 'answered' ? '✓ Répondu' : '⏳ En attente'}
                      </span>
                      {msg.type === 'activation_request' && (
                        <span className="px-2 py-1 rounded-full text-xs font-semibold bg-orange-100 text-orange-700 flex items-center gap-1">
                          <i className="ri-file-upload-line"></i> Activation
                        </span>
                      )}
                    </div>
                  </div>
                  <p className="text-sm text-gray-600 line-clamp-2 mb-1">{msg.message}</p>
                  {msg.screenshotProof && (
                    <div className="mt-1 flex items-center gap-1 text-xs text-orange-600">
                      <i className="ri-image-line"></i>
                      <span className="font-semibold">Capture d'écran jointe</span>
                    </div>
                  )}
                  <p className="text-xs text-gray-400">{formatDate(msg.timestamp)}</p>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Détail et réponse */}
        <div className="bg-white rounded-xl shadow overflow-hidden flex flex-col" style={{ minHeight: 400 }}>
          {selectedMessage ? (
            <>
              <div className="p-4 border-b border-gray-100 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center">
                    <i className="ri-user-line text-teal-600 text-lg"></i>
                  </div>
                  <div>
                    <p className="font-bold text-gray-800">{selectedMessage.username}</p>
                    <p className="text-xs text-gray-400">{selectedMessage.userId}</p>
                  </div>
                </div>
                <button onClick={() => setConfirmDelete(selectedMessage.id)}
                  className="p-2 text-gray-400 hover:text-red-500 cursor-pointer transition" title="Supprimer ce message">
                  <i className="ri-delete-bin-line text-lg"></i>
                </button>
              </div>

              <div className="flex-1 p-4 overflow-y-auto bg-gray-50 space-y-4">
                <div className="flex justify-end">
                  <div className="max-w-[85%]">
                    {selectedMessage.type === 'activation_request' && (
                      <div className="mb-2 px-3 py-1 bg-orange-100 border border-orange-300 rounded-full text-orange-700 text-xs font-bold inline-flex items-center gap-1">
                        <i className="ri-file-upload-line"></i> Demande d'activation manuelle
                      </div>
                    )}
                    <div className="bg-teal-600 text-white rounded-2xl rounded-tr-sm px-4 py-3 shadow-sm">
                      <p className="text-sm">{selectedMessage.message}</p>
                      {selectedMessage.screenshotProof && (
                        <div className="mt-3 pt-3 border-t border-teal-500">
                          <p className="text-xs text-teal-200 mb-2 font-semibold flex items-center gap-1">
                            <i className="ri-image-line"></i> Capture d'écran du paiement :
                          </p>
                          <img src={selectedMessage.screenshotProof} alt="Capture d'écran"
                            className="max-h-40 rounded-lg border-2 border-teal-400 cursor-pointer hover:opacity-90 transition"
                            onClick={() => { setModalScreenshot(selectedMessage.screenshotProof || ''); setShowScreenshotModal(true); }} />
                          <p className="text-xs text-teal-200 mt-1 flex items-center gap-1">
                            <i className="ri-zoom-in-line"></i> Cliquez pour agrandir
                          </p>
                        </div>
                      )}
                    </div>
                    <p className="text-xs text-gray-400 text-right mt-1">{formatDate(selectedMessage.timestamp)}</p>
                  </div>
                </div>

                {selectedMessage.response && (
                  <div className="flex justify-start">
                    <div className="max-w-[85%]">
                      <div className="flex items-center gap-2 mb-1">
                        <div className="w-6 h-6 bg-orange-100 rounded-full flex items-center justify-center">
                          <i className="ri-customer-service-2-line text-orange-600 text-xs"></i>
                        </div>
                        <span className="text-xs font-bold text-orange-600">Votre réponse</span>
                      </div>
                      <div className="bg-white border border-gray-200 rounded-2xl rounded-tl-sm px-4 py-3 shadow-sm">
                        <p className="text-sm text-gray-700">{selectedMessage.response}</p>
                      </div>
                    </div>
                  </div>
                )}
                <div ref={bottomRef} />
              </div>

              <div className="p-4 border-t border-gray-100 bg-white">
                {successMsg && (
                  <div className="mb-3 px-3 py-2 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm flex items-center gap-2">
                    <i className="ri-checkbox-circle-line"></i> {successMsg}
                  </div>
                )}

                {selectedMessage.type === 'activation_request' && selectedMessage.status === 'pending' && (
                  <div className="mb-4 bg-orange-50 border-2 border-orange-200 rounded-xl p-4">
                    <div className="flex items-start gap-3 mb-3">
                      <i className="ri-information-line text-orange-600 text-xl mt-0.5"></i>
                      <div className="flex-1">
                        <p className="text-sm font-bold text-orange-800 mb-1">Demande d'activation manuelle</p>
                        <p className="text-xs text-orange-700">Vérifiez la capture ci-dessus et activez le compte si le paiement est valide.</p>
                      </div>
                    </div>
                    <button
                      onClick={() => setConfirmActivate({ phone: selectedMessage.userId, name: selectedMessage.username })}
                      className="w-full py-3 px-4 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-bold rounded-lg text-sm transition cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 shadow">
                      <i className="ri-checkbox-circle-line text-lg"></i>
                      Activer le compte de {selectedMessage.username}
                    </button>
                  </div>
                )}

                <p className="text-xs font-bold text-gray-500 mb-2">
                  {selectedMessage.status === 'answered' ? 'MODIFIER LA RÉPONSE' : "RÉPONDRE À L'UTILISATEUR"}
                </p>
                <textarea value={responseText} onChange={e => setResponseText(e.target.value)}
                  placeholder="Écrivez votre réponse ici..." rows={4} maxLength={500}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-teal-400 outline-none resize-none" />
                <div className="flex items-center justify-between mt-2">
                  <span className="text-xs text-gray-400">{responseText.length}/500</span>
                  <button onClick={sendResponse} disabled={!responseText.trim() || sending}
                    className="px-5 py-2 bg-teal-600 text-white rounded-lg text-sm font-semibold hover:bg-teal-700 disabled:bg-gray-300 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap flex items-center gap-2 transition">
                    <i className="ri-send-plane-fill"></i>
                    {sending ? 'Envoi...' : selectedMessage.status === 'answered' ? 'Modifier la réponse' : 'Envoyer la réponse'}
                  </button>
                </div>
              </div>
            </>
          ) : (
            <div className="flex items-center justify-center flex-1 p-8">
              <div className="text-center">
                <i className="ri-message-3-line text-6xl text-gray-200 mb-3 block"></i>
                <p className="text-gray-400 text-sm">Sélectionnez un message<br />pour voir les détails et répondre</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modal image agrandie */}
      {showScreenshotModal && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => setShowScreenshotModal(false)}>
          <div className="relative max-w-4xl max-h-[90vh] overflow-auto">
            <button onClick={() => setShowScreenshotModal(false)}
              className="absolute top-4 right-4 w-10 h-10 bg-white rounded-full flex items-center justify-center text-gray-800 hover:bg-gray-100 transition cursor-pointer shadow-lg z-10">
              <i className="ri-close-line text-xl"></i>
            </button>
            <img src={modalScreenshot} alt="Capture d'écran agrandie" className="rounded-lg shadow-2xl" onClick={e => e.stopPropagation()} />
          </div>
        </div>
      )}
    </div>
  );
}
